import { useEffect, useState, type FormEvent } from 'react';

import { SparkInput } from '../components/SparkInput';
import { SparkMessage } from '../components/SparkMessage';
import { ApiError, listAgencies, saveAgency } from '../lib/api';
import type { Agency, AgencyDevice } from '../lib/types';

/** Per-PCC host settings: the terminals and printers. */
export function AgenciesPage() {
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState<Agency | null>(null);

  const load = () => {
    setLoading(true);
    listAgencies()
      .then((result) => {
        setAgencies(result.agencies);
        setError('');
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'The agency list could not be loaded.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-header__title">Agencies</h1>
          <p className="page-header__subtitle">
            The name each PCC prints on its <span className="mono">DQB*</span> sales report, and the terminals and
            printers it sees with <span className="mono">PE*</span> and can assign with <span className="mono">DSIV</span>. Ticketing permission is set per user on the Users page, by root.
          </p>
        </div>
      </div>

      {error && <SparkMessage variant="error">{error}</SparkMessage>}
      {notice && <SparkMessage variant="success">{notice}</SparkMessage>}

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>PCC</th>
              <th>Name</th>
              <th>Terminals</th>
              <th>Printers</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && agencies.length === 0 && (
              <tr>
                <td colSpan={5} className="table__empty">
                  Loading agencies…
                </td>
              </tr>
            )}
            {!loading && agencies.length === 0 && (
              <tr>
                <td colSpan={5} className="table__empty">
                  No agencies yet. An agency appears here once a user is signed to its PCC.
                </td>
              </tr>
            )}
            {agencies.map((agency) => {
              const printers = agency.devices.filter((d) => d.type === 'PTR');
              return (
                <tr key={agency.pcc}>
                  <td className="mono">{agency.pcc}</td>
                  <td>{agency.name || '—'}</td>
                  <td>{agency.devices.length - printers.length}</td>
                  <td className="mono">{printers.map((p) => p.lniata).join(', ') || '—'}</td>
                  <td>
                    <div className="table__actions">
                      <button type="button" className="spark-btn-link" onClick={() => setEditing(agency)}>
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <AgencyDialog
          agency={editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            setNotice(`Agency ${saved.pcc} saved.`);
            load();
          }}
        />
      )}
    </>
  );
}

function AgencyDialog({
  agency,
  onClose,
  onSaved,
}: {
  agency: Agency;
  onClose: () => void;
  onSaved: (agency: Agency) => void;
}) {
  const [name, setName] = useState(agency.name ?? '');
  const [devices, setDevices] = useState<AgencyDevice[]>(agency.devices.map((d) => ({ ...d })));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const update = (index: number, patch: Partial<AgencyDevice>) =>
    setDevices((current) => current.map((d, i) => (i === index ? { ...d, ...patch } : d)));

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const result = await saveAgency({ pcc: agency.pcc, name, devices });
      onSaved(result.agency);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The agency could not be saved.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="agency-dialog-title">
        <div className="dialog__header">
          <h2 className="dialog__title" id="agency-dialog-title">
            Agency {agency.pcc}
          </h2>
          <button type="button" className="dialog__close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="dialog__body">
            {error && <SparkMessage variant="error">{error}</SparkMessage>}

            <SparkInput
              label="Agency name"
              placeholder="DHAKA POPULAR TRAVELS"
              value={name}
              maxLength={40}
              onChange={(event) => setName(event.target.value.toUpperCase())}
              disabled={submitting}
            />

            <div className="agency-devices agency-devices--flush">
              <div className="agency-devices__head">
                <span>LNIATA</span>
                <span>Type</span>
                <span />
              </div>
              {devices.map((device, index) => (
                <div key={index} className="agency-devices__row">
                  <input
                    className="ref-cell mono"
                    value={device.lniata}
                    placeholder="26EC7E"
                    aria-label="LNIATA"
                    onChange={(event) => update(index, { lniata: event.target.value.toUpperCase() })}
                    disabled={submitting}
                  />
                  <select
                    className="ref-cell"
                    value={device.type}
                    aria-label="Device type"
                    onChange={(event) => update(index, { type: event.target.value as AgencyDevice['type'] })}
                    disabled={submitting}
                  >
                    <option value="CRT">CRT — terminal</option>
                    <option value="PTR">PTR — printer</option>
                  </select>
                  <button
                    type="button"
                    className="spark-btn-link"
                    onClick={() => setDevices((current) => current.filter((_, i) => i !== index))}
                    disabled={submitting}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="spark-btn spark-btn--md spark-btn--secondary agency-devices__add"
                onClick={() => setDevices((current) => [...current, { lniata: '', type: 'CRT' }])}
                disabled={submitting}
              >
                Add device
              </button>
            </div>
          </div>
          <div className="dialog__footer">
            <button
              type="button"
              className="spark-btn spark-btn--md spark-btn--secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="spark-btn spark-btn--md spark-btn--primary" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
